import React, { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  initI18n,
  useTranslation,
  formatNumber as i18nFormatNumber,
  getCurrentLocale
} from '../../shared/i18n';
import { useCurriculum, useDeleteActivity } from './hooks/useCurriculum';
import { ChildProfile } from './components/ChildProfile';
import { ActivityList } from './components/ActivityList';
import { LearningSummary } from './components/LearningSummary';
import { RecentActivity } from './components/RecentActivity';
import { DeleteConfirmModal } from './components/DeleteConfirmModal';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * CurriculumPageContent - Main content component for the curriculum page
 */
const CurriculumPageContent = ({
  childId,
  flashNotice,
  flashAlert,
  newActivityPath
}) => {
  const { data, isLoading, error } = useCurriculum(childId);
  const deleteActivityMutation = useDeleteActivity(childId);
  const { t } = useTranslation();

  // State for delete confirmation modal
  const [activityToDelete, setActivityToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState(null);

  // Helper function to format numbers using locale-aware formatting
  const formatNumber = (num) => {
    return i18nFormatNumber(num);
  };

  // Helper function to format relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    const locale = getCurrentLocale();

    if (diffMins < 1) {
      return locale === 'vi' ? 'vừa xong' : 'just now';
    } else if (diffMins < 60) {
      return locale === 'vi' ? `${diffMins} phút trước` : `${diffMins} minutes ago`;
    } else if (diffHours < 24) {
      return locale === 'vi' ? `${diffHours} giờ trước` : `${diffHours} hours ago`;
    } else {
      return locale === 'vi' ? `${diffDays} ngày trước` : `${diffDays} days ago`;
    }
  };

  const handleDeleteClick = (activity) => {
    setActivityToDelete(activity);
  };

  const handleDeleteConfirm = async () => {
    if (!activityToDelete) return;

    setIsDeleting(true);
    try {
      await deleteActivityMutation.mutateAsync(activityToDelete.id);
      setActivityToDelete(null);
      setDeleteSuccessMessage(t('curriculum.delete_success'));
    } catch (err) {
      console.error('Failed to delete activity:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setActivityToDelete(null);
  };

  if (isLoading) {
    return (
      <div className="bg-light min-vh-100">
        <div className="container py-4">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-light min-vh-100">
        <div className="container py-4">
          <div className="alert alert-danger">
            {t('common.error')}: {error.message}
          </div>
        </div>
      </div>
    );
  }

  // Guard against undefined data (can happen during route transitions when childId becomes undefined)
  if (!data) {
    return null;
  }

  const { child, activities, recent_completions, stats, paths } = data;

  return (
    <div className="curriculum-page" data-curriculum-page>
      {/* Flash messages */}
      {flashNotice && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {flashNotice}
          <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
        </div>
      )}
      {flashAlert && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {flashAlert}
          <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
        </div>
      )}
      {deleteSuccessMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {deleteSuccessMessage}
          <button type="button" className="btn-close" onClick={() => setDeleteSuccessMessage(null)} aria-label="Close"></button>
        </div>
      )}

      <div className="bg-light min-vh-100">
        <div className="container py-4">
          {/* Page Header */}
          <div className="mb-4">
            <Link to="/" className="btn btn-link text-secondary p-0 mb-3">
              <i className="bi bi-arrow-left me-2"></i>
            </Link>
            <h2 className="h1 fw-bold mb-2">{t('curriculum.page_title', { name: child.username })}</h2>
            <p className="text-muted">{t('curriculum.customize_learning', { name: child.username })}</p>
          </div>

          <div className="row g-4">
            {/* Main Column */}
            <div className="col-lg-8">
              {/* Child Profile Card */}
              <ChildProfile
                child={child}
                t={t}
              />

              {/* Activity List */}
              <ActivityList
                activities={activities}
                childId={childId}
                newActivityPath={newActivityPath}
                onDeleteClick={handleDeleteClick}
                t={t}
              />
            </div>

            {/* Sidebar Column */}
            <div className="col-lg-4">
              <LearningSummary
                stats={stats}
                t={t}
                formatNumber={formatNumber}
              />

              <RecentActivity
                completions={recent_completions}
                t={t}
                formatRelativeTime={formatRelativeTime}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!activityToDelete}
        activityName={activityToDelete?.name || ''}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isDeleting={isDeleting}
        t={t}
      />
    </div>
  );
};

export const CurriculumPage = ({ locale = 'vi', ...props }) => {
  // Initialize i18n with the provided locale
  initI18n(locale);

  return (
    <QueryClientProvider client={queryClient}>
      <CurriculumPageContent {...props} />
    </QueryClientProvider>
  );
};

// Export content component for use within existing QueryClientProvider context
export { CurriculumPageContent };

export default CurriculumPage;
